import { useState } from 'react';
import { Film, Phone, Mail, MapPin, Send } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return; // Silently fail for invalid email
    }
    try {
      await axios.post(`${API}/newsletter`, { email });
      setSubscribed(true);
    } catch { /* ignore */ }
  };
  return (
    <footer className="bg-[#0A0A0A] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          <div>
            <a href="#top" className="flex items-center gap-1.5 mb-4">
              <Film className="w-6 h-6 text-[#FF007A]" />
              <span className="font-bebas text-2xl tracking-tighter leading-none">
                <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(135deg, #F59E0B, #EA580C)' }}>VEGAS</span>
                <span className="font-caveat text-lg text-[#FF007A] mx-0.5">to</span>
                <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(135deg, #06B6D4, #3B82F6)' }}>HOLLYWOOD</span>
              </span>
            </a>
            <p className="font-outfit text-sm text-white/60 leading-relaxed">
              The premier all-inclusive day trip from Las Vegas to Hollywood, California. Trusted by over 10,000 travelers worldwide since 2010.
            </p>
          </div>

          <div>
            <h4 className="font-bebas text-lg tracking-tight uppercase mb-4">Quick Links</h4>
            <nav className="space-y-2">
              <a href="#itinerary" onClick={(e) => { e.preventDefault(); window.location.hash = 'itinerary'; }} className="block font-outfit text-sm text-white/60 hover:text-white transition-colors">Tour Itinerary</a>
              <a href="#features" onClick={(e) => { e.preventDefault(); window.location.hash = 'features'; }} className="block font-outfit text-sm text-white/60 hover:text-white transition-colors">Why Choose Us</a>
              <a href="#reviews" onClick={(e) => { e.preventDefault(); window.location.hash = 'reviews'; }} className="block font-outfit text-sm text-white/60 hover:text-white transition-colors">Guest Reviews</a>
              <a href="#faq" onClick={(e) => { e.preventDefault(); window.location.hash = 'faq'; }} className="block font-outfit text-sm text-white/60 hover:text-white transition-colors">FAQ</a>
              <a href="#booking" onClick={(e) => { e.preventDefault(); window.location.hash = 'booking'; }} className="block font-outfit text-sm text-white/60 hover:text-white transition-colors">Book Now</a>
              <a href="#contact" onClick={(e) => { e.preventDefault(); window.location.hash = 'contact'; }} className="block font-outfit text-sm text-white/60 hover:text-white transition-colors">Contact Us</a>
              <a href="#/blog" onClick={(e) => { e.preventDefault(); window.location.hash = '/blog'; }} className="block font-outfit text-sm text-[#FFD600] hover:text-white transition-colors">Travel Blog & Guides</a>
            </nav>
          </div>

          <div>
            <h4 className="font-bebas text-lg tracking-tight uppercase mb-4">Contact</h4>
            <div className="space-y-3">
              <a href="tel:+18777418390" className="flex items-center gap-2 font-outfit text-sm text-white/60 hover:text-white transition-colors" data-testid="footer-phone-tollfree">
                <Phone className="w-4 h-4 text-[#FF007A]" />
                1-877-741-8390 (Toll-Free)
              </a>
              <a href="tel:+17029415144" className="flex items-center gap-2 font-outfit text-sm text-white/60 hover:text-white transition-colors" data-testid="footer-phone-intl">
                <Phone className="w-4 h-4 text-[#00E5FF]" />
                +1 (702) 941-5144 (International)
              </a>
              <a href="mailto:rsvp@vegas2hollywood.com" className="flex items-center gap-2 font-outfit text-sm text-white/60 hover:text-white transition-colors" data-testid="footer-email">
                <Mail className="w-4 h-4 text-[#FFD600]" />
                rsvp@vegas2hollywood.com
              </a>
              <div className="flex items-center gap-2 font-outfit text-sm text-white/60">
                <MapPin className="w-4 h-4 text-[#9D00FF]" />
                Las Vegas, Nevada
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-bebas text-lg tracking-tight uppercase mb-4">Book Your Tour</h4>
            <p className="font-outfit text-sm text-white/60 mb-4">
              Starting from just $215 per person. Breakfast and lunch included. Free hotel pickup.
            </p>
            <a href="#booking" onClick={(e) => { e.preventDefault(); window.location.hash = 'booking'; }} className="btn-primary px-6 py-3 text-sm block text-center mb-5" data-testid="footer-book-cta">
              BOOK NOW - $215
            </a>
            <h4 className="font-bebas text-lg tracking-tight uppercase mb-2">Tour Deals & Updates</h4>
            {subscribed ? (
              <p className="font-outfit text-sm text-green-400" data-testid="newsletter-success">Subscribed! Check your inbox.</p>
            ) : (
              <form onSubmit={handleSubscribe} className="flex gap-2" data-testid="newsletter-form" noValidate>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Your email"
                  className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 font-outfit text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-[#FF007A]"
                  data-testid="newsletter-email-input"
                />
                <button type="submit" className="bg-[#FF007A] rounded-lg px-3 py-2 text-white" data-testid="newsletter-submit-btn">
                  <Send className="w-4 h-4" />
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="border-t border-white/10 pt-8">
          <div className="font-outfit text-xs text-white/40 leading-relaxed mb-6 space-y-3">
            <p>
              <strong>Vegas to Hollywood</strong> operates the premier all-inclusive sightseeing tour from Las Vegas, Nevada to Hollywood, California.
              Our full day guided tour is the top-rated day trip from Las Vegas, featuring complimentary breakfast, a sit-down restaurant lunch,
              luxury air-conditioned transportation, and visits to Santa Monica Beach and Pier, Warner Bros Studios, Beverly Hills, Rodeo Drive,
              Sunset Boulevard, celebrity mansions, and the Hollywood Walk of Fame. Whether you are searching for the best things to do in Las Vegas,
              planning a family vacation day trip, organizing a convention companion experience, or seeking luxury sightseeing tours from Las Vegas —
              we deliver an unforgettable California adventure.
            </p>
            <p>
              Our Las Vegas tours are designed for every traveler: Las Vegas group tours for 15 to 55+ guests, Las Vegas family tours with kid-friendly itineraries,
              Las Vegas private tours in luxury SUVs, Las Vegas convention companion tours, Las Vegas bachelor and bachelorette party tours, Las Vegas birthday celebration tours,
              Las Vegas church group tours, Las Vegas corporate team building tours, Las Vegas school and educational tours, and Las Vegas couples tours for romantic getaways.
              We are the most trusted Las Vegas tour company, Las Vegas tour operator, and Las Vegas travel service with over 10,000 verified five-star reviews from guests worldwide.
            </p>
            <p>
              We welcome guests from across the United States and Canada, including visitors from Toronto, Vancouver, Calgary, Edmonton, Montreal, Ottawa, Winnipeg,
              New York, Los Angeles, Chicago, Houston, Phoenix, Dallas, San Antonio, Miami, Denver, Atlanta, Seattle, Boston, Nashville, Detroit, Portland, Minneapolis,
              Orlando, San Francisco, Philadelphia, Tampa, Charlotte, Austin, San Diego, Indianapolis, Columbus, Memphis, Oklahoma City, Louisville, Albuquerque, Tucson,
              Sacramento, Salt Lake City, Reno, Honolulu, and hundreds of other cities. International travelers from the United Kingdom, Australia, Germany, France, Japan,
              South Korea, Brazil, Mexico, India, and worldwide are warmly welcomed on our tours every day of the year.
            </p>
            <p>
              Popular searches that lead to our tour: things to do in Las Vegas, Las Vegas tours, Las Vegas day trips, Las Vegas attractions, Las Vegas sightseeing,
              Las Vegas activities, Las Vegas excursions, Las Vegas bus tours, Las Vegas van tours, Las Vegas to Hollywood, Vegas to LA tour, Las Vegas to Los Angeles,
              Hollywood tour from Las Vegas, Beverly Hills tour from Las Vegas, Santa Monica tour from Las Vegas, Walk of Fame tour, celebrity homes tour Hollywood,
              Rodeo Drive tour, Warner Bros Studios tour from Las Vegas, best day trip from Las Vegas, Las Vegas vacation ideas, Las Vegas trip planner,
              Las Vegas entertainment, Las Vegas travel deals, Las Vegas tour packages, Las Vegas tour deals, cheap Las Vegas tours, affordable Las Vegas tours,
              luxury Las Vegas tours, Las Vegas tour reviews, Las Vegas guided tours, Las Vegas sightseeing packages, all inclusive Las Vegas tours,
              Las Vegas tour with lunch, Las Vegas tour with breakfast, Las Vegas hotel pickup tours, free hotel pickup Las Vegas tours,
              Las Vegas Strip tours, Downtown Las Vegas tours, Fremont Street tours, Las Vegas Boulevard tours, Las Vegas tourism, Las Vegas travel guide,
              California day trip from Las Vegas, Nevada to California tour, desert road trip Las Vegas, Las Vegas to Santa Monica, Las Vegas to Beverly Hills,
              Las Vegas to Rodeo Drive, Las Vegas to Hollywood Sign, Las Vegas to Hollywood Boulevard, Las Vegas to Sunset Boulevard.
            </p>
            <p>
              Additional services and tour types: Las Vegas group tour discounts, Las Vegas large group tours, Las Vegas small group tours, Las Vegas intimate tours,
              Las Vegas VIP tours, Las Vegas premium tours, Las Vegas deluxe tours, Las Vegas standard tours, Las Vegas economy tours, Las Vegas budget tours,
              Las Vegas first class tours, Las Vegas all day tours, Las Vegas full day tours, Las Vegas half day tours, Las Vegas morning tours, Las Vegas evening tours,
              Las Vegas weekend tours, Las Vegas weekday tours, Las Vegas holiday tours, Las Vegas spring break tours, Las Vegas summer tours, Las Vegas winter tours,
              Las Vegas fall tours, Las Vegas seasonal tours, Las Vegas year round tours, Las Vegas daily tours, Las Vegas tour schedule, Las Vegas tour availability,
              Las Vegas tour calendar, book Las Vegas tour online, reserve Las Vegas tour, Las Vegas tour reservation, Las Vegas tour booking, Las Vegas tour confirmation,
              Las Vegas instant booking tours, Las Vegas same day tour booking, Las Vegas last minute tours, Las Vegas advance booking tours,
              Las Vegas honeymoon tours, Las Vegas anniversary tours, Las Vegas retirement celebration tours, Las Vegas graduation tours,
              Las Vegas prom tours, Las Vegas quinceañera tours, Las Vegas wedding party tours, Las Vegas reunion tours, Las Vegas milestone celebration tours.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="font-outfit text-xs text-white/40">
              &copy; {new Date().getFullYear()} Vegas to Hollywood Tours. All rights reserved.
            </p>
            <p className="font-outfit text-xs text-white/40">
              24/7 Reservations Open | Serving Las Vegas Since 2010 | 10,000+ Five-Star Reviews
            </p>
          </div>
        </div>
      </div>

      {/* Mobile Sticky CTA */}
      <div className="mobile-cta" data-testid="mobile-sticky-cta">
        <a href="tel:+18777418390" className="flex items-center gap-1.5 text-white font-outfit font-bold text-sm">
          <Phone className="w-4 h-4 text-[#FFD600]" />
          CALL
        </a>
        <a href="#booking" className="btn-primary px-6 py-2.5 text-xs" data-testid="mobile-sticky-book">
          BOOK NOW - $215
        </a>
      </div>
    </footer>
  );
};

export default Footer;
